import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Divider,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Favorite as FavoriteIcon,
  DonutLarge as DonutLargeIcon,
  Settings as SettingsIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import GradingConfigDialog from './GradingConfigDialog';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { useGetUsersQuery, MonthlyIncentiveGoal } from '../../redux/vmsApi';
import { useGetAllPEARReviewsQuery } from '../../redux/pearReviewApi';
import { useGetJobDescriptionDepartmentsQuery, useGetJobDescriptionsActiveQuery, useGetMyJobDescriptionQuery } from '../../redux/jobDescriptionApi';
import { useGetActiveBusinessUnitsQuery } from '../../redux/businessUnitApi';
import { useGetOrgChartWithBSCQuery, OrgChartNode } from '../../redux/balancedScorecardApi';
import { useGetAllLifeWheelsQuery, useGetAllSpearReviewsQuery } from '../../redux/vmsApi';
import { useGetCastingToolDepartmentStatsQuery } from '../../redux/castingToolApi';
import { PERFORMANCE_RATINGS, type PerformanceRating } from './bscTypes';

// Generate quarters for select dropdown
const generateQuarterOptions = (year: number) => {
  return [
    { value: `Q1 ${year}`, label: `Q1 ${year}` },
    { value: `Q2 ${year}`, label: `Q2 ${year}` },
    { value: `Q3 ${year}`, label: `Q3 ${year}` },
    { value: `Q4 ${year}`, label: `Q4 ${year}` },
  ];
};

// Generate annual option for a year
const generateAnnualOption = (year: number) => {
  return { value: `Annual ${year}`, label: `Annual ${year}` };
};

// Get the previous quarter (e.g., if current is Q1 2026, returns "Q4 2025")
const getPreviousQuarter = (): string => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  const currentYear = now.getFullYear();
  
  // Determine current quarter (1-4)
  const currentQuarter = Math.floor(currentMonth / 3) + 1;
  
  // Calculate previous quarter
  if (currentQuarter === 1) {
    // Q1 -> previous is Q4 of last year
    return `Q4 ${currentYear - 1}`;
  } else {
    return `Q${currentQuarter - 1} ${currentYear}`;
  }
};

// Convert quarterly period to monthly periods (for Life Wheel and SPEAR)
// Q1 2026 -> ["January 2026", "February 2026", "March 2026"]
// Annual 2026 -> ["January 2026", "February 2026", ..., "December 2026"]
const getMonthlyPeriodsForQuarter = (quarterPeriod: string): string[] => {
  const monthNames = [
    'January', 'February', 'March',
    'April', 'May', 'June',
    'July', 'August', 'September',
    'October', 'November', 'December'
  ];

  // Check for annual period format
  const annualMatch = quarterPeriod.match(/^Annual\s+(\d{4})$/);
  if (annualMatch) {
    const year = annualMatch[1];
    return monthNames.map(month => `${month} ${year}`);
  }

  // Quarterly period format
  const match = quarterPeriod.match(/Q(\d)\s+(\d{4})/);
  if (!match) return [];
  
  const quarter = parseInt(match[1]);
  const year = match[2];
  
  const startMonth = (quarter - 1) * 3;
  return [
    `${monthNames[startMonth]} ${year}`,
    `${monthNames[startMonth + 1]} ${year}`,
    `${monthNames[startMonth + 2]} ${year}`,
  ];
};

// Get quarterly periods for PEAR filtering
// Q1 2026 -> ["Q1 2026"]
// Annual 2026 -> ["Q1 2026", "Q2 2026", "Q3 2026", "Q4 2026"]
const getQuarterlyPeriodsForPEAR = (period: string): string[] => {
  const annualMatch = period.match(/^Annual\s+(\d{4})$/);
  if (annualMatch) {
    const year = annualMatch[1];
    return [`Q1 ${year}`, `Q2 ${year}`, `Q3 ${year}`, `Q4 ${year}`];
  }
  return [period];
};

// Check if a monthly period (e.g., "March 2026") is March 2026 or later
// Monthly Incentive Goals feature started in March 2026
const isMonthMarch2026OrLater = (monthPeriod: string): boolean => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
  const match = monthPeriod.match(/^(\w+)\s+(\d{4})$/);
  if (!match) return false;
  const [, monthName, yearStr] = match;
  const year = parseInt(yearStr);
  const monthIndex = monthNames.indexOf(monthName); // 0-11
  // March 2026 = year 2026, monthIndex 2
  // Returns true if (year > 2026) OR (year === 2026 AND monthIndex >= 2)
  return (year > 2026) || (year === 2026 && monthIndex >= 2);
};

// Check if a period (quarterly or annual) is Q1 2026 or later
// Goal status section should only show for Q1 2026 onwards
const isPeriodQ1_2026OrLater = (period: string): boolean => {
  // Handle Annual format: "Annual 2026"
  const annualMatch = period.match(/^Annual\s+(\d{4})$/);
  if (annualMatch) {
    return parseInt(annualMatch[1]) >= 2026;
  }
  // Handle Quarterly format: "Q1 2026"
  const quarterMatch = period.match(/^Q(\d)\s+(\d{4})$/);
  if (quarterMatch) {
    const quarter = parseInt(quarterMatch[1]);
    const year = parseInt(quarterMatch[2]);
    // Q1 2026 or later: year > 2026, OR year === 2026
    return year > 2026 || year === 2026;
  }
  return false;
};

type GoalAchievementStatus = 'achieved' | 'progress' | 'failed' | 'pending';

const getGoalAchievementStatus = (goal?: MonthlyIncentiveGoal): GoalAchievementStatus => {
  if (!goal) return 'pending';

  if (goal.completed) return 'achieved';

  const actualPercentage = goal.actualPercentage;
  if (actualPercentage === undefined || actualPercentage === null) return 'pending';

  if (actualPercentage >= 100) return 'achieved';
  if (actualPercentage >= 80) return 'progress';
  return 'failed';
};

// Grade color helper
const getGradeColor = (grade?: string): string => {
  if (!grade) return '#9e9e9e';
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

// Score color helper
const getScoreColor = (score?: number): string => {
  if (score === undefined || score === null) return '#9e9e9e';
  if (score >= 110) return '#033e79';
  if (score >= 100) return '#17A2B8';
  if (score >= 85) return '#32CD32';
  if (score >= 70) return '#FFC107';
  return '#DC3545';
};

/** Display scores > 110 as "110+%" */
const formatScore = (score: number, decimals = 1): string => {
  return score > 110 ? '110+%' : `${score.toFixed(decimals)}%`;
};

// Grade distribution type
interface GradeCount {
  grade: string;
  count: number;
  percentage: number;
  color: string;
}

// Department summary type
interface DepartmentSummary {
  name: string;
  employeeCount: number;
  avgScore: number;
  avgGrade: string;
  completedReviews: number;
  pendingReviews: number;
  submittedReviews: number;
  approvedReviews: number;
  topPerformer?: { name: string; score: number };
  // Goal achievement metrics
  goalsAchieved: number;  // 100%+
  goalsProgress: number;  // 80-99%
  goalsFailed: number;    // <80%
  goalsPending: number;   // No data
}

// Employee performance type
interface EmployeePerformance {
  id: string;
  name: string;
  email: string;
  department?: string;
  designation?: string;
  bscScore?: number;
  bscGrade?: string;
  pearScore?: number;
  pearGrade?: string;
  hasBSC?: boolean;
  hasPEAR?: boolean;
  hasLifeWheel?: boolean;
  hasSpear?: boolean;
  lifeWheelCount?: number;
  spearCount?: number;
  // Monthly goal tracking
  monthlyGoals: {
    month: string;
    shortMonth: string;
    status: GoalAchievementStatus;
    actualPercentage?: number;
    description?: string;
  }[];
}

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  secondarySubtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: number;
  showWarning?: boolean;
  warningText?: string;
  onClick?: () => void;
}> = ({ title, value, subtitle, secondarySubtitle, icon, color, trend, showWarning, warningText, onClick }) => (
  <Card
    sx={{
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      } : {},
      border: showWarning ? '2px solid #ff9800' : undefined,
    }}
    onClick={onClick}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {subtitle}
            </Typography>
          )}
          {secondarySubtitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {secondarySubtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: alpha(color, 0.1),
            color: color,
          }}
        >
          {icon}
        </Box>
      </Stack>
      {trend !== undefined && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          {trend >= 0 ? (
            <TrendingUpIcon fontSize="small" color="success" />
          ) : (
            <TrendingDownIcon fontSize="small" color="error" />
          )}
          <Typography variant="caption" color={trend >= 0 ? 'success.main' : 'error.main'}>
            {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% vs last quarter
          </Typography>
        </Stack>
      )}
      {showWarning && warningText && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <WarningIcon fontSize="small" sx={{ color: '#ff9800' }} />
          <Typography variant="caption" color="warning.main">
            {warningText}
          </Typography>
        </Stack>
      )}
    </CardContent>
  </Card>
);

// Monthly Metric Card Component - shows monthly breakdown for Life Wheel and SPEAR
interface MonthlyStats {
  month: string;
  shortMonth: string;
  submissions: number;
  expected: number;
}

const MonthlyMetricCard: React.FC<{
  title: string;
  monthlyStats: MonthlyStats[];
  totalSubmitters: number;
  totalEmployees: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}> = ({ title, monthlyStats, totalSubmitters, totalEmployees, icon, color, onClick }) => {
  const totalSubmissions = monthlyStats.reduce((sum, m) => sum + m.submissions, 0);
  const totalExpected = monthlyStats.reduce((sum, m) => sum + m.expected, 0);
  const completionRate = totalExpected > 0 ? (totalSubmissions / totalExpected) * 100 : 0;
  const showWarning = completionRate < 50;
  
  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
        border: showWarning ? '2px solid #ff9800' : undefined,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ pb: '8px !important' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalSubmitters} of {totalEmployees} employees submitted
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: alpha(color, 0.1),
              color: color,
            }}
          >
            {icon}
          </Box>
        </Stack>
        
        {/* Monthly breakdown */}
        <Stack spacing={0.5}>
          {monthlyStats.map((stat) => {
            const monthCompletion = stat.expected > 0 ? (stat.submissions / stat.expected) * 100 : 0;
            return (
              <Box key={stat.month}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" fontWeight="medium" sx={{ minWidth: 35 }}>
                    {stat.shortMonth}
                  </Typography>
                  <Box sx={{ flex: 1, mx: 1 }}>
                    <Tooltip title={`${stat.submissions}/${stat.expected} (${monthCompletion.toFixed(0)}%)`}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(monthCompletion, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          backgroundColor: alpha(color, 0.15),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: monthCompletion >= 100 ? '#4caf50' : color,
                            borderRadius: 1,
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 45, textAlign: 'right' }}>
                    {stat.submissions}/{stat.expected}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
        
        {/* Total summary */}
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Total: {totalSubmissions}/{totalExpected}
          </Typography>
          <Chip
            size="small"
            label={`${completionRate.toFixed(0)}%`}
            sx={{
              backgroundColor: completionRate >= 80 ? '#4caf50' : completionRate >= 50 ? '#ff9800' : '#f44336',
              color: 'white',
              fontWeight: 'bold',
              height: 20,
              fontSize: '0.7rem',
            }}
          />
        </Stack>
        
        {showWarning && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
            <WarningIcon fontSize="small" sx={{ color: '#ff9800', fontSize: 14 }} />
            <Typography variant="caption" color="warning.main">
              Low participation
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

// Grade Distribution Chart (horizontal bar)
const GradeDistributionChart: React.FC<{ data: GradeCount[] }> = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Stacked bar */}
      <Box sx={{ display: 'flex', height: 32, borderRadius: 1, overflow: 'hidden', mb: 2 }}>
        {data.map((item, index) => (
          <Tooltip key={item.grade} title={`${item.grade}: ${item.count} (${item.percentage.toFixed(1)}%)`}>
            <Box
              sx={{
                width: `${item.percentage}%`,
                backgroundColor: item.color,
                minWidth: item.count > 0 ? 8 : 0,
                transition: 'all 0.3s',
                '&:hover': { opacity: 0.8 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {data.filter(d => d.count > 0).map((item) => (
          <Chip
            key={item.grade}
            label={`${item.grade}: ${item.count}`}
            size="small"
            sx={{
              backgroundColor: item.color,
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Goal Distribution Chart by Month
interface GoalMonthData {
  month: string;
  shortMonth: string;
  total: number;
  achieved: number;
  progress: number;
  failed: number;
  pending: number;
  withGoalsSet: number; // Employees who have set a goal for this month
}

interface TopFiveMonthData {
  month: string;
  shortMonth: string;
  total: number;
  setCount: number;
  completedCount: number;
  managerScoredCount: number;
}

interface TopFiveDrilldownRow {
  userId: string;
  employeeName: string;
  department: string;
  designation: string;
  month: string;
  spearId?: string;
  hasSubmission: boolean;
  hasTopFiveSet: boolean;
  isTopFiveCompleted: boolean;
  isManagerScored: boolean;
  submittedAt?: string;
}

const GoalDistributionChart: React.FC<{ data: GoalMonthData[] }> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No goal data available for this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {data.map((item) => {
        const achievedPct = item.withGoalsSet > 0 ? (item.achieved / item.withGoalsSet) * 100 : 0;
        const progressPct = item.withGoalsSet > 0 ? (item.progress / item.withGoalsSet) * 100 : 0;
        const failedPct = item.withGoalsSet > 0 ? (item.failed / item.withGoalsSet) * 100 : 0;
        const pendingPct = item.withGoalsSet > 0 ? (item.pending / item.withGoalsSet) * 100 : 0;
        const notSetPct = item.total > 0 ? ((item.total - item.withGoalsSet) / item.total) * 100 : 0;
        
        return (
          <Box key={item.month} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" fontWeight="medium">{item.shortMonth || item.month}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="Achieved">
                  <Chip size="small" label={item.achieved} sx={{ bgcolor: '#1976d2', color: 'white', minWidth: 28 }} />
                </Tooltip>
                <Tooltip title="Progress">
                  <Chip size="small" label={item.progress} sx={{ bgcolor: '#ff9800', color: 'white', minWidth: 28 }} />
                </Tooltip>
                <Tooltip title="Failed">
                  <Chip size="small" label={item.failed} sx={{ bgcolor: '#f44336', color: 'white', minWidth: 28 }} />
                </Tooltip>
                <Typography variant="caption" color="text.secondary">
                  {item.withGoalsSet}/{item.total} set
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ display: 'flex', height: 20, borderRadius: 1, overflow: 'hidden', bgcolor: '#e0e0e0' }}>
              <Tooltip title={`Achieved: ${item.achieved}`}>
                <Box sx={{ width: `${achievedPct * (item.withGoalsSet / item.total)}%`, backgroundColor: '#1976d2', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`Progress: ${item.progress}`}>
                <Box sx={{ width: `${progressPct * (item.withGoalsSet / item.total)}%`, backgroundColor: '#ff9800', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`Failed: ${item.failed}`}>
                <Box sx={{ width: `${failedPct * (item.withGoalsSet / item.total)}%`, backgroundColor: '#f44336', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`Pending: ${item.pending}`}>
                <Box sx={{ width: `${pendingPct * (item.withGoalsSet / item.total)}%`, backgroundColor: '#9e9e9e', transition: 'all 0.3s' }} />
              </Tooltip>
            </Box>
          </Box>
        );
      })}
      {/* Legend */}
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#1976d2', borderRadius: '50%' }} />
          <Typography variant="caption">Achieved (100%+)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#ff9800', borderRadius: '50%' }} />
          <Typography variant="caption">Progress (80-99%)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#f44336', borderRadius: '50%' }} />
          <Typography variant="caption">Failed (&lt;80%)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#9e9e9e', borderRadius: '50%' }} />
          <Typography variant="caption">Pending</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#e0e0e0', borderRadius: '50%' }} />
          <Typography variant="caption">No Goal Set</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const TopFiveDistributionChart: React.FC<{
  data: TopFiveMonthData[];
  onMonthClick?: (month: string) => void;
}> = ({ data, onMonthClick }) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No Top 5 data available for this period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {data.map((item) => {
        const managerScoredOnly = Math.min(item.managerScoredCount, item.completedCount);
        const completedOnly = Math.max(0, item.completedCount - managerScoredOnly);
        const setOnly = Math.max(0, item.setCount - item.completedCount);
        const notSet = Math.max(0, item.total - item.setCount);

        const setOnlyPct = item.total > 0 ? (setOnly / item.total) * 100 : 0;
        const completedOnlyPct = item.total > 0 ? (completedOnly / item.total) * 100 : 0;
        const managerScoredPct = item.total > 0 ? (managerScoredOnly / item.total) * 100 : 0;
        const notSetPct = item.total > 0 ? (notSet / item.total) * 100 : 0;

        return (
          <Paper
            key={item.month}
            variant="outlined"
            sx={{
              mb: 1.5,
              p: 1.25,
              borderRadius: 2,
              cursor: onMonthClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              '&:hover': onMonthClick ? {
                borderColor: '#0d47a1',
                boxShadow: '0 8px 20px rgba(13,71,161,0.12)',
                transform: 'translateY(-1px)',
              } : {},
            }}
            onClick={() => onMonthClick?.(item.month)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>{item.shortMonth}</Typography>
              <Typography variant="caption" color="text.secondary">
                {item.completedCount}/{item.total} completed
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', height: 18, borderRadius: 1, overflow: 'hidden', bgcolor: '#eceff1' }}>
              <Tooltip title={`Top 5 Set (In Progress): ${setOnly}`}>
                <Box sx={{ width: `${setOnlyPct}%`, backgroundColor: '#0288d1', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`Completed & Self-Scored: ${completedOnly}`}>
                <Box sx={{ width: `${completedOnlyPct}%`, backgroundColor: '#2e7d32', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`Manager Scored: ${managerScoredOnly}`}>
                <Box sx={{ width: `${managerScoredPct}%`, backgroundColor: '#6a1b9a', transition: 'all 0.3s' }} />
              </Tooltip>
              <Tooltip title={`No Top 5 Set: ${notSet}`}>
                <Box sx={{ width: `${notSetPct}%`, backgroundColor: '#b0bec5', transition: 'all 0.3s' }} />
              </Tooltip>
            </Box>
          </Paper>
        );
      })}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#0288d1', borderRadius: '50%' }} />
          <Typography variant="caption">Top 5 Set</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#2e7d32', borderRadius: '50%' }} />
          <Typography variant="caption">Completed & Self-Scored</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#6a1b9a', borderRadius: '50%' }} />
          <Typography variant="caption">Manager Scored</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: '#b0bec5', borderRadius: '50%' }} />
          <Typography variant="caption">Not Set</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

// BSC Perspectives Chart (scale 0-110%)
const PerspectivesChart: React.FC<{ data: { name: string; score: number }[] }> = ({ data }) => (
  <Box>
    {data.map((perspective) => {
      // Scale to 110% max - normalize score so 110% fills the bar
      const normalizedValue = Math.min((perspective.score / 110) * 100, 100);
      return (
        <Box key={perspective.name} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{perspective.name}</Typography>
            <Typography variant="body2" fontWeight="bold" color={getScoreColor(perspective.score)}>
              {formatScore(perspective.score)}
            </Typography>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={normalizedValue}
              sx={{
                height: 10,
                borderRadius: 1,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getScoreColor(perspective.score),
                },
              }}
            />
            {/* 100% marker line */}
            <Box
              sx={{
                position: 'absolute',
                left: `${(100 / 110) * 100}%`,
                top: 0,
                height: '100%',
                width: '2px',
                backgroundColor: '#666',
                opacity: 0.5,
              }}
            />
          </Box>
        </Box>
      );
    })}
  </Box>
);

// Department Card (clickable)
const DepartmentCard: React.FC<{
  department: DepartmentSummary;
  onClick: () => void;
  showGoals: boolean;
}> = ({ department, onClick, showGoals }) => (
  <Card
    sx={{
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
        borderColor: 'primary.main',
      },
      border: '1px solid transparent',
    }}
    onClick={onClick}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {department.name}
        </Typography>
        <NavigateNextIcon color="action" />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Employees</Typography>
          <Typography variant="h6">{department.employeeCount}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Avg Score</Typography>
          <Typography variant="h6" color={getScoreColor(department.avgScore)}>
            {department.avgScore > 0 ? formatScore(department.avgScore) : 'N/A'}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary">Pending</Typography>
          <Typography variant="body1" color="warning.main">{department.pendingReviews}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary">Submitted</Typography>
          <Typography variant="body1" color="info.main">{department.submittedReviews}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary">Approved</Typography>
          <Typography variant="body1" color="success.main">{department.approvedReviews}</Typography>
        </Grid>
      </Grid>

      {/* Goal Achievement Summary - only show for Q1 2026+ */}
      {showGoals && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>🎯 Goal Status</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
            {/* Total Set */}
            <Chip
              label={`${department.goalsAchieved + department.goalsProgress + department.goalsFailed} Set`}
              size="small"
              sx={{ backgroundColor: '#607d8b', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
            />
            {department.goalsAchieved > 0 && (
              <Chip
                label={`${department.goalsAchieved} Achieved`}
                size="small"
                sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
              />
            )}
            {department.goalsProgress > 0 && (
              <Chip
                label={`${department.goalsProgress} Progress`}
                size="small"
                sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
              />
            )}
            {department.goalsFailed > 0 && (
              <Chip
                label={`${department.goalsFailed} Not Met`}
                size="small"
                sx={{ backgroundColor: '#f44336', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
              />
            )}
            {department.goalsPending > 0 && (
              <Chip
                label={`${department.goalsPending} Pending`}
                size="small"
                sx={{ backgroundColor: '#9e9e9e', color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}
              />
            )}
          </Stack>
        </Box>
      )}

      {department.topPerformer && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e0e0e0' }}>
          <Typography variant="caption" color="text.secondary">Top Performer</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrophyIcon fontSize="small" sx={{ color: '#FFD700' }} />
            <Typography variant="body2" fontWeight="bold">
              {department.topPerformer.name}
            </Typography>
            <Chip
              label={formatScore(department.topPerformer.score, 0)}
              size="small"
              sx={{ backgroundColor: getScoreColor(department.topPerformer.score), color: 'white' }}
            />
          </Stack>
        </Box>
      )}
    </CardContent>
  </Card>
);

// Employee Row Component (clickable)
const EmployeeRow: React.FC<{
  employee: EmployeePerformance;
  rank: number;
  onClick: () => void;
}> = ({ employee, rank, onClick }) => (
  <TableRow
    hover
    sx={{ cursor: 'pointer' }}
    onClick={onClick}
  >
    <TableCell>
      <Chip
        label={rank}
        size="small"
        sx={{
          backgroundColor: rank <= 3 ? '#FFD700' : rank <= 10 ? '#C0C0C0' : '#CD7F32',
          color: rank <= 3 ? 'black' : 'white',
          fontWeight: 'bold',
          minWidth: 32,
        }}
      />
    </TableCell>
    <TableCell>
      <Typography variant="body2" fontWeight="bold">{employee.name}</Typography>
      <Typography variant="caption" color="text.secondary">{employee.designation || 'N/A'}</Typography>
    </TableCell>
    <TableCell>{employee.department || 'N/A'}</TableCell>
    <TableCell align="center">
      {employee.hasBSC && employee.bscScore !== undefined ? (
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <Typography variant="body2">{formatScore(employee.bscScore)}</Typography>
          <Chip
            label={employee.bscGrade}
            size="small"
            sx={{ backgroundColor: getGradeColor(employee.bscGrade), color: 'white', minWidth: 36 }}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      )}
    </TableCell>
    <TableCell align="center">
      {employee.hasPEAR && employee.pearScore !== undefined ? (
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="center">
          <Typography variant="body2">{formatScore(employee.pearScore)}</Typography>
          <Chip
            label={employee.pearGrade}
            size="small"
            sx={{ backgroundColor: getGradeColor(employee.pearGrade), color: 'white', minWidth: 36 }}
          />
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      )}
    </TableCell>
    {/* Goals summary column - only count March 2026+ months */}
    <TableCell align="center">
      {(() => {
        const validGoals = employee.monthlyGoals.filter(g => isMonthMarch2026OrLater(g.month));
        const achieved = validGoals.filter(g => g.status === 'achieved').length;
        const total = validGoals.length;
        const hasGoals = validGoals.some(g => g.status !== 'pending');
        if (!hasGoals || total === 0) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Tooltip title={`${achieved} of ${total} goals achieved`}>
            <Chip
              label={`${achieved}/${total}`}
              size="small"
              sx={{
                backgroundColor: achieved === total && total > 0 ? '#4caf50' : achieved > 0 ? '#ff9800' : '#f44336',
                color: 'white',
                fontWeight: 'bold',
                minWidth: 45,
              }}
            />
          </Tooltip>
        );
      })()}
    </TableCell>
    <TableCell align="center">
      <NavigateNextIcon color="action" />
    </TableCell>
  </TableRow>
);

// Main CEO Dashboard Component
const CEODashboard: React.FC = () => {
  const router = useRouter();
  const currentUser = useAppSelector(state => state.auth.user);
  
  // Check if user has company-wide view (CEO, COO, admin)
  const hasCompanyWideView = currentUser?.roles?.includes('admin') || 
    currentUser?.roles?.includes('ceo') || 
    currentUser?.roles?.includes('coo');
  const currentYear = new Date().getFullYear();
  const availablePeriods = useMemo(() => [
    generateAnnualOption(currentYear),
    ...generateQuarterOptions(currentYear),
    generateAnnualOption(currentYear - 1),
    ...generateQuarterOptions(currentYear - 1),
  ], [currentYear]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(getPreviousQuarter());
  
  // Business Unit filter - "WestProp Holdings" is the default, "All" shows all units
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string>('WestProp Holdings');
  
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [employeeModalTab, setEmployeeModalTab] = useState(0);
  const [modalTitle, setModalTitle] = useState('Employee Report Status');
  const [showGradingConfig, setShowGradingConfig] = useState(false);
  const [showTopFiveDialog, setShowTopFiveDialog] = useState(false);
  const [selectedTopFiveMonth, setSelectedTopFiveMonth] = useState<string | null>(null);
  const [topFiveFilter, setTopFiveFilter] = useState<'all' | 'completed' | 'scored' | 'pending'>('all');
  
  // Sorting state for Top Performers table
  const [topSortField, setTopSortField] = useState<'bsc' | 'pear' | 'goals'>('bsc');
  const [topSortOrder, setTopSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Sorting state for Needs Attention table
  const [attentionSortField, setAttentionSortField] = useState<'bsc' | 'pear' | 'goals'>('bsc');
  const [attentionSortOrder, setAttentionSortOrder] = useState<'asc' | 'desc'>('asc');

  // Check if selected period is annual
  const isAnnualPeriod = selectedPeriod.startsWith('Annual');

  // Calculate the monthly period for Casting Tool based on selected quarter
  // Shows the last month of the quarter (or current month if it's the current quarter)
  const castingToolPeriod = useMemo(() => {
    const monthNames = [
      'January', 'February', 'March',
      'April', 'May', 'June',
      'July', 'August', 'September',
      'October', 'November', 'December'
    ];
    
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Handle Annual period - use current month
    if (selectedPeriod.startsWith('Annual')) {
      return `${monthNames[currentMonth]} ${currentYear}`;
    }
    
    // Parse quarterly period
    const match = selectedPeriod.match(/Q(\d)\s+(\d{4})/);
    if (!match) return `${monthNames[currentMonth]} ${currentYear}`;
    
    const quarter = parseInt(match[1]);
    const year = parseInt(match[2]);
    
    // Get the last month index of the quarter (0-indexed)
    const lastMonthOfQuarter = quarter * 3 - 1; // Q1→2 (March), Q2→5 (June), Q3→8 (Sep), Q4→11 (Dec)
    
    // If this is the current year and we're still in this quarter, use current month
    if (year === currentYear && Math.floor(currentMonth / 3) + 1 === quarter) {
      return `${monthNames[currentMonth]} ${year}`;
    }
    
    // Otherwise use the last month of the quarter
    return `${monthNames[lastMonthOfQuarter]} ${year}`;
  }, [selectedPeriod]);

  // Handler for opening modal with specific tab
  const openEmployeeModal = (tab: number, title?: string) => {
    setEmployeeModalTab(tab);
    setModalTitle(title || 'Employee Report Status');
    setShowEmployeeModal(true);
  };

  const openTopFiveDialog = (month?: string) => {
    setSelectedTopFiveMonth(month || null);
    setTopFiveFilter('all');
    setShowTopFiveDialog(true);
  };

  // Fetch data using existing APIs
  const { data: users, isLoading: loadingUsers } = useGetUsersQuery();
  const { data: departments, isLoading: loadingDepartments } = useGetJobDescriptionDepartmentsQuery();
  const { data: jobDescriptions, isLoading: loadingJDs } = useGetJobDescriptionsActiveQuery();
  const { data: myJobDescription, isLoading: loadingMyJD } = useGetMyJobDescriptionQuery();
  const { data: pearReviews, isLoading: loadingPEAR, refetch: refetchPEAR } = useGetAllPEARReviewsQuery();
  const { data: orgChartData, isLoading: loadingOrgChart, refetch: refetchOrgChart } = useGetOrgChartWithBSCQuery({ period: selectedPeriod });
  const { data: allLifeWheels, isLoading: loadingLifeWheel, refetch: refetchLifeWheel } = useGetAllLifeWheelsQuery();
  const { data: allSpearReviews, isLoading: loadingSpear, refetch: refetchSpear } = useGetAllSpearReviewsQuery();
  const { data: castingToolStats, isLoading: loadingCastingTool, refetch: refetchCastingTool } = useGetCastingToolDepartmentStatsQuery({ reviewPeriod: castingToolPeriod });
  const { data: businessUnitsData = [] } = useGetActiveBusinessUnitsQuery();

  // Build BUSINESS_UNITS array from API data (with 'All' option)
  const BUSINESS_UNITS = useMemo(() => {
    const units = businessUnitsData.map(u => u.name);
    return ['All', ...units];
  }, [businessUnitsData]);

  const isLoading = loadingUsers || loadingDepartments || loadingJDs || loadingMyJD || loadingPEAR || loadingOrgChart || loadingLifeWheel || loadingSpear || loadingCastingTool;

  // Get current user's department and job description ID
  const myDepartment = myJobDescription?.department;
  const myJobDescriptionId = myJobDescription?.id;

  // Helper function to recursively find all subordinates (direct and indirect reports)
  const getAllSubordinateIds = useMemo(() => {
    if (!orgChartData || !myJobDescriptionId) return new Set<string>();
    
    const subordinateIds = new Set<string>();
    
    // Create a map of job description ID to their direct reports
    const reportsToMap = new Map<string, string[]>();
    orgChartData.forEach(node => {
      const reportsTo = node.jobDescription.reportsToJobDescriptionId;
      if (reportsTo) {
        if (!reportsToMap.has(reportsTo)) {
          reportsToMap.set(reportsTo, []);
        }
        reportsToMap.get(reportsTo)!.push(node.jobDescription.id);
      }
    });
    
    // Create a map of job description ID to employee ID
    const jdToEmployeeMap = new Map<string, string>();
    orgChartData.forEach(node => {
      if (node.employee?.id) {
        jdToEmployeeMap.set(node.jobDescription.id, node.employee.id);
      }
    });
    
    // Recursively find all subordinates
    const findSubordinates = (jdId: string) => {
      const directReports = reportsToMap.get(jdId) || [];
      directReports.forEach(reportJdId => {
        const employeeId = jdToEmployeeMap.get(reportJdId);
        if (employeeId) {
          subordinateIds.add(employeeId);
        }
        // Recursively find their subordinates
        findSubordinates(reportJdId);
      });
    };
    
    findSubordinates(myJobDescriptionId);
    return subordinateIds;
  }, [orgChartData, myJobDescriptionId]);

  // Filter org chart data based on access level and business unit
  const filteredOrgChartData = useMemo(() => {
    if (!orgChartData) return [];
    
    // First apply business unit filter
    let filtered = orgChartData;
    if (selectedBusinessUnit !== 'All') {
      filtered = orgChartData.filter(node => {
        // If job description has no businessUnit, default to 'WestProp Holdings'
        const nodeBusinessUnit = node.jobDescription.businessUnit || 'WestProp Holdings';
        return nodeBusinessUnit === selectedBusinessUnit;
      });
    }
    
    if (hasCompanyWideView) return filtered;
    
    // For non-company-wide users, filter to show:
    // 1. Employees in the same department
    // 2. Direct and indirect reports (subordinates)
    return filtered.filter(node => {
      const employeeId = node.employee?.id;
      const nodeDepartment = node.jobDescription.department;
      
      // Include if in same department
      if (myDepartment && nodeDepartment === myDepartment) return true;
      
      // Include if this is a subordinate
      if (employeeId && getAllSubordinateIds.has(employeeId)) return true;
      
      return false;
    });
  }, [orgChartData, hasCompanyWideView, myDepartment, getAllSubordinateIds, selectedBusinessUnit]);

  // Filter departments based on access level and business unit
  const filteredDepartments = useMemo(() => {
    if (!departments) return [];
    
    // Get departments that exist in the filtered org chart data (respects business unit filter)
    const departmentsInFilteredData = new Set<string>();
    filteredOrgChartData.forEach(node => {
      if (node.jobDescription.department) {
        departmentsInFilteredData.add(node.jobDescription.department);
      }
    });
    
    if (hasCompanyWideView) {
      // For company-wide view, return only departments that have employees in the selected business unit
      return departments.filter(dept => departmentsInFilteredData.has(dept));
    }
    
    // For non-company-wide users, show only:
    // 1. Their own department
    // 2. Departments that have their subordinates
    const subordinateDepartments = new Set<string>();
    if (orgChartData) {
      orgChartData.forEach(node => {
        if (node.employee?.id && getAllSubordinateIds.has(node.employee.id) && node.jobDescription.department) {
          subordinateDepartments.add(node.jobDescription.department);
        }
      });
    }
    
    return departments.filter(dept => 
      (dept === myDepartment || subordinateDepartments.has(dept)) && departmentsInFilteredData.has(dept)
    );
  }, [departments, hasCompanyWideView, myDepartment, getAllSubordinateIds, orgChartData, filteredOrgChartData]);

  // Get monthly periods for the selected quarter (for Life Wheel and SPEAR filtering)
  const monthlyPeriods = useMemo(() => getMonthlyPeriodsForQuarter(selectedPeriod), [selectedPeriod]);
  
  // Get quarterly periods for PEAR filtering (handles annual vs quarterly)
  const quarterlyPeriods = useMemo(() => getQuarterlyPeriodsForPEAR(selectedPeriod), [selectedPeriod]);

  // Helper to check if period is pre-2026 (for filtering employees not yet hired)
  const periodYear = parseInt(selectedPeriod.match(/\d{4}/)?.[0] || '2026');
  const isPre2026 = periodYear < 2026;

  // Helper function to check if a node should be excluded based on hire date
  // Wilma joined Jan 2026 - exclude her from pre-2026 periods
  const shouldExcludeNode = (node: OrgChartNode): boolean => {
    if (isPre2026 && node.employee?.email?.toLowerCase() === 'wilma@westprop.com') {
      return true;
    }
    return false;
  };

  // Process data for metrics
  const metrics = useMemo(() => {
    if (!users || !filteredOrgChartData || filteredOrgChartData.length === 0) return null;

    // Get employee IDs who have job descriptions assigned (for consistent filtering)
    // Use filtered org chart data for restricted views
    const employeesWithJD = new Set(
      filteredOrgChartData
        .filter(node => node.employee?.id)
        .map(node => node.employee!.id)
    );

    // Filter PEAR reviews by period (supports quarterly and annual) - only submitted/approved, not draft
    // Also filter by employees in the filtered org chart
    const periodPEARs = pearReviews?.filter(p => 
      quarterlyPeriods.includes(p.reviewPeriod) && 
      (p.status === 'submitted' || p.status === 'approved') &&
      p.employeeId && employeesWithJD.has(p.employeeId)
    ) || [];

    // Filter Life Wheel by monthly periods - ONLY for employees with JDs
    const periodLifeWheels = allLifeWheels?.filter(
      lw => monthlyPeriods.includes(lw.reviewPeriod) && lw.userId && employeesWithJD.has(lw.userId)
    ) || [];
    
    // Filter SPEAR by monthly periods - ONLY for employees with JDs
    const periodSpearReviews = allSpearReviews?.filter(
      sr => monthlyPeriods.includes(sr.reviewPeriod) && sr.userId && employeesWithJD.has(sr.userId)
    ) || [];

    // Get BSC scores from filtered org chart (excluding employees not yet hired)
    const bscScores = filteredOrgChartData
      .filter(node => !shouldExcludeNode(node))
      .filter(node => node.latestBSC?.totalWeightedScore !== undefined)
      .map(node => ({
        userId: node.employee?.id,
        score: node.latestBSC!.totalWeightedScore!,
        grade: node.latestBSC!.finalGrade!,
        department: node.jobDescription?.department,
        status: node.latestBSC!.status,
      }));

    // Count approved BSCs and PEARs
    const bscApproved = bscScores.filter(b => b.status === 'approved').length;
    const pearApproved = periodPEARs.filter(p => p.status === 'approved').length;

    // Calculate grade distribution
    const gradeDistribution: GradeCount[] = PERFORMANCE_RATINGS.map(rating => ({
      grade: rating.grade,
      count: bscScores.filter(s => s.grade === rating.grade).length,
      percentage: bscScores.length > 0
        ? (bscScores.filter(s => s.grade === rating.grade).length / bscScores.length) * 100
        : 0,
      color: rating.colorCode || '#9e9e9e',
    }));

    // Calculate averages
    const avgBSCScore = bscScores.length > 0
      ? bscScores.reduce((sum, s) => sum + s.score, 0) / bscScores.length
      : 0;

    const avgPEARScore = periodPEARs.length > 0
      ? periodPEARs.reduce((sum, p) => sum + (p.overallScore || 0), 0) / periodPEARs.length
      : 0;

    // Department summaries - use filtered departments
    const departmentSummaries: DepartmentSummary[] = (filteredDepartments || []).map(dept => {
      // Filter out employees not yet hired in this period
      const deptNodes = filteredOrgChartData
        .filter(n => n.jobDescription?.department === dept)
        .filter(n => !shouldExcludeNode(n));
      const deptEmployeeIds = new Set(deptNodes.filter(n => n.employee?.id).map(n => n.employee!.id));
      const deptBSCScores = deptNodes
        .filter(n => n.latestBSC?.totalWeightedScore !== undefined)
        .map(n => ({
          name: n.employee?.fullName || 'Unknown',
          score: n.latestBSC!.totalWeightedScore!,
          status: n.latestBSC!.status,
        }));

      // Count submitted and approved BSCs
      const submittedReviews = deptBSCScores.filter(b => b.status === 'submitted').length;
      const approvedReviews = deptBSCScores.filter(b => b.status === 'approved').length;

      // Goal achievement metrics for this department (only March 2026+ months)
      const deptSpearReviews = periodSpearReviews.filter(sr => 
        sr.userId && deptEmployeeIds.has(sr.userId) && isMonthMarch2026OrLater(sr.reviewPeriod)
      );
      let goalsAchieved = 0, goalsProgress = 0, goalsFailed = 0, goalsPending = 0;
      deptSpearReviews.forEach(sr => {
        const goal = sr.monthlyIncentiveGoal;
        const status = getGoalAchievementStatus(goal as MonthlyIncentiveGoal);
        if (status === 'achieved') goalsAchieved++;
        else if (status === 'progress') goalsProgress++;
        else if (status === 'failed') goalsFailed++;
        else goalsPending++;
      });

      const topPerformer = deptBSCScores.length > 0
        ? deptBSCScores.reduce((max, curr) => curr.score > max.score ? curr : max, deptBSCScores[0])
        : undefined;

      return {
        name: dept,
        employeeCount: deptNodes.filter(n => n.employee).length,
        avgScore: deptBSCScores.length > 0
          ? deptBSCScores.reduce((sum, s) => sum + s.score, 0) / deptBSCScores.length
          : 0,
        avgGrade: 'B', // Simplified
        completedReviews: deptBSCScores.length,
        pendingReviews: deptNodes.filter(n => n.employee && !n.latestBSC).length,
        submittedReviews,
        approvedReviews,
        topPerformer,
        goalsAchieved,
        goalsProgress,
        goalsFailed,
        goalsPending,
      };
    }).sort((a, b) => b.avgScore - a.avgScore);

    // C-Suite: Direct reports to CEO (only for users with company-wide view)
    // Find CEO's job description ID - try multiple approaches:
    // 1. Find node with no reportsToJobDescriptionId (top of hierarchy)
    // 2. Find node with title containing "CEO" or "Chief Executive"
    let ceoNode = filteredOrgChartData.find(n => !n.jobDescription.reportsToJobDescriptionId);
    if (!ceoNode) {
      ceoNode = filteredOrgChartData.find(n => 
        n.jobDescription.title?.toLowerCase().includes('ceo') ||
        n.jobDescription.title?.toLowerCase().includes('chief executive officer')
      );
    }
    const ceoJobDescriptionId = ceoNode?.jobDescription.id;
    
    // Get direct reports to CEO (excluding employees not yet hired)
    // Also check for "Chief" titles that typically report directly to CEO
    const cSuiteNodes = ceoJobDescriptionId 
      ? filteredOrgChartData
          .filter(n => n.jobDescription.reportsToJobDescriptionId === ceoJobDescriptionId)
          .filter(n => !shouldExcludeNode(n))
      : filteredOrgChartData.filter(n => 
          n.jobDescription.title?.toLowerCase().includes('chief') && 
          !n.jobDescription.title?.toLowerCase().includes('ceo') &&
          !n.jobDescription.title?.toLowerCase().includes('chief executive officer') &&
          !shouldExcludeNode(n)
        );
    
    // Create C-Suite summary if user has company-wide view and there are direct reports
    if (hasCompanyWideView && cSuiteNodes.length > 0) {
      const cSuiteEmployeeIds = new Set(cSuiteNodes.filter(n => n.employee?.id).map(n => n.employee!.id));
      const cSuiteBSCScores = cSuiteNodes
        .filter(n => n.latestBSC?.totalWeightedScore !== undefined)
        .map(n => ({
          name: n.employee?.fullName || 'Unknown',
          score: n.latestBSC!.totalWeightedScore!,
          status: n.latestBSC!.status,
        }));

      const cSuiteSubmitted = cSuiteBSCScores.filter(b => b.status === 'submitted').length;
      const cSuiteApproved = cSuiteBSCScores.filter(b => b.status === 'approved').length;

      // Goal achievement metrics for C-Suite (only March 2026+ months)
      const cSuiteSpearReviews = periodSpearReviews.filter(sr => 
        sr.userId && cSuiteEmployeeIds.has(sr.userId) && isMonthMarch2026OrLater(sr.reviewPeriod)
      );
      let cSuiteGoalsAchieved = 0, cSuiteGoalsProgress = 0, cSuiteGoalsFailed = 0, cSuiteGoalsPending = 0;
      cSuiteSpearReviews.forEach(sr => {
        const goal = sr.monthlyIncentiveGoal;
        const status = getGoalAchievementStatus(goal as MonthlyIncentiveGoal);
        if (status === 'achieved') cSuiteGoalsAchieved++;
        else if (status === 'progress') cSuiteGoalsProgress++;
        else if (status === 'failed') cSuiteGoalsFailed++;
        else cSuiteGoalsPending++;
      });

      const cSuiteTopPerformer = cSuiteBSCScores.length > 0
        ? cSuiteBSCScores.reduce((max, curr) => curr.score > max.score ? curr : max, cSuiteBSCScores[0])
        : undefined;

      const cSuiteSummary: DepartmentSummary = {
        name: 'C-Suite',
        employeeCount: cSuiteNodes.filter(n => n.employee).length,
        avgScore: cSuiteBSCScores.length > 0
          ? cSuiteBSCScores.reduce((sum, s) => sum + s.score, 0) / cSuiteBSCScores.length
          : 0,
        avgGrade: 'B',
        completedReviews: cSuiteBSCScores.length,
        pendingReviews: cSuiteNodes.filter(n => n.employee && !n.latestBSC).length,
        submittedReviews: cSuiteSubmitted,
        approvedReviews: cSuiteApproved,
        topPerformer: cSuiteTopPerformer,
        goalsAchieved: cSuiteGoalsAchieved,
        goalsProgress: cSuiteGoalsProgress,
        goalsFailed: cSuiteGoalsFailed,
        goalsPending: cSuiteGoalsPending,
      };

      // Add C-Suite at the beginning of department summaries
      departmentSummaries.unshift(cSuiteSummary);
    }

    // Employee performance list - use filtered org chart
    const employeePerformance: EmployeePerformance[] = filteredOrgChartData
      .filter(node => node.employee)
      // Filter out employees not yet hired in this period
      .filter(node => !shouldExcludeNode(node))
      .map(node => {
        const pearReview = periodPEARs.find(p => p.employeeId === node.employee?.id);
        const employeeLifeWheels = periodLifeWheels.filter(lw => lw.userId === node.employee?.id);
        const employeeSpearReviews = periodSpearReviews.filter(sr => sr.userId === node.employee?.id);
        
        // Map monthly goals for this employee
        const monthlyGoals = monthlyPeriods.map(month => {
          const spear = employeeSpearReviews.find(sr => sr.reviewPeriod === month);
          const goal = spear?.monthlyIncentiveGoal as MonthlyIncentiveGoal | undefined;
          return {
            month,
            shortMonth: month.substring(0, 3),
            status: getGoalAchievementStatus(goal),
            actualPercentage: goal?.actualPercentage,
            description: goal?.description,
          };
        });
        
        return {
          id: node.employee!.id,
          name: node.employee!.fullName,
          email: node.employee!.email,
          department: node.jobDescription?.department,
          designation: node.jobDescription?.title,
          bscScore: node.latestBSC?.totalWeightedScore,
          bscGrade: node.latestBSC?.finalGrade,
          pearScore: pearReview?.overallScore,
          pearGrade: pearReview?.finalGrade,
          hasBSC: !!node.latestBSC,
          hasPEAR: !!pearReview,
          hasLifeWheel: employeeLifeWheels.length > 0,
          hasSpear: employeeSpearReviews.length > 0,
          lifeWheelCount: employeeLifeWheels.length,
          spearCount: employeeSpearReviews.length,
          monthlyGoals,
        };
      })
      .sort((a, b) => (b.bscScore || 0) - (a.bscScore || 0));

    // Calculate completion rates - use unique employees with JDs for consistency
    const totalEmployees = employeesWithJD.size;
    const lifeWheelSubmitters = new Set(periodLifeWheels.map(lw => lw.userId)).size;
    const spearSubmitters = new Set(periodSpearReviews.map(sr => sr.userId)).size;
    
    // Monthly breakdown stats for Life Wheel and SPEAR
    const lifeWheelByMonth = monthlyPeriods.map(month => {
      const monthSubmissions = allLifeWheels?.filter(
        lw => lw.reviewPeriod === month && lw.userId && employeesWithJD.has(lw.userId)
      ) || [];
      const shortMonth = month.substring(0, 3); // "October 2025" -> "Oct"
      return {
        month,
        shortMonth,
        submissions: monthSubmissions.length,
        expected: totalEmployees,
      };
    });
    
    const spearByMonth = monthlyPeriods.map(month => {
      const monthSubmissions = allSpearReviews?.filter(
        sr => sr.reviewPeriod === month && sr.userId && employeesWithJD.has(sr.userId)
      ) || [];
      const shortMonth = month.substring(0, 3);
      return {
        month,
        shortMonth,
        submissions: monthSubmissions.length,
        expected: totalEmployees,
      };
    });

    const employeeMetaById = new Map(
      filteredOrgChartData
        .filter(node => node.employee?.id)
        .map(node => [
          node.employee!.id,
          {
            name: node.employee!.fullName,
            department: node.jobDescription?.department || '-',
            designation: node.jobDescription?.title || '-',
          },
        ])
    );

    const evaluateTopFive = (spear: any) => {
      const accomplishments = Array.isArray(spear?.accomplishments) ? spear.accomplishments : [];
      const filledCount = accomplishments.filter((a: any) => a?.title && String(a.title).trim().length > 0).length;
      const selfRatedCount = accomplishments.filter((a: any) => a?.rating && String(a.rating).trim().length > 0).length;
      const managerRatedCount = accomplishments.filter((a: any) => a?.managerRating && String(a.managerRating).trim().length > 0).length;

      const hasTopFiveSet = filledCount > 0;
      const isTopFiveCompleted = filledCount >= 5 && selfRatedCount >= 5;
      const isManagerScored = managerRatedCount >= 5 || spear?.status === 'approved' || spear?.status === 'rejected';

      return { hasTopFiveSet, isTopFiveCompleted, isManagerScored };
    };

    const topFiveByMonth: TopFiveMonthData[] = monthlyPeriods.map(month => {
      const monthSpears = periodSpearReviews.filter(sr => sr.reviewPeriod === month);
      let setCount = 0;
      let completedCount = 0;
      let managerScoredCount = 0;

      monthSpears.forEach((spear: any) => {
        const topFive = evaluateTopFive(spear);
        if (topFive.hasTopFiveSet) setCount++;
        if (topFive.isTopFiveCompleted) completedCount++;
        if (topFive.isManagerScored) managerScoredCount++;
      });

      return {
        month,
        shortMonth: month.substring(0, 3),
        total: totalEmployees,
        setCount,
        completedCount,
        managerScoredCount,
      };
    });

    const topFiveDrilldownByMonth: Record<string, TopFiveDrilldownRow[]> = {};
    monthlyPeriods.forEach(month => {
      const monthRowsMap = new Map<string, TopFiveDrilldownRow>();
      const monthSpears = periodSpearReviews.filter(sr => sr.reviewPeriod === month);

      monthSpears.forEach((spear: any) => {
        const userId = spear.userId;
        if (!userId || !employeesWithJD.has(userId)) return;
        const meta = employeeMetaById.get(userId);
        const topFive = evaluateTopFive(spear);

        monthRowsMap.set(userId, {
          userId,
          employeeName: meta?.name || 'Unknown Employee',
          department: meta?.department || '-',
          designation: meta?.designation || '-',
          month,
          spearId: spear.id,
          hasSubmission: true,
          hasTopFiveSet: topFive.hasTopFiveSet,
          isTopFiveCompleted: topFive.isTopFiveCompleted,
          isManagerScored: topFive.isManagerScored,
          submittedAt: spear.updatedAt || spear.createdAt,
        });
      });

      employeesWithJD.forEach(userId => {
        if (monthRowsMap.has(userId)) return;
        const meta = employeeMetaById.get(userId);
        monthRowsMap.set(userId, {
          userId,
          employeeName: meta?.name || 'Unknown Employee',
          department: meta?.department || '-',
          designation: meta?.designation || '-',
          month,
          hasSubmission: false,
          hasTopFiveSet: false,
          isTopFiveCompleted: false,
          isManagerScored: false,
        });
      });

      topFiveDrilldownByMonth[month] = Array.from(monthRowsMap.values())
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    });

    const topFiveStatsLocal = {
      totalExpected: totalEmployees * monthlyPeriods.length,
      setCount: topFiveByMonth.reduce((sum, m) => sum + m.setCount, 0),
      completedCount: topFiveByMonth.reduce((sum, m) => sum + m.completedCount, 0),
      managerScoredCount: topFiveByMonth.reduce((sum, m) => sum + m.managerScoredCount, 0),
    };

    const topFiveSetRate = topFiveStatsLocal.totalExpected > 0
      ? (topFiveStatsLocal.setCount / topFiveStatsLocal.totalExpected) * 100
      : 0;
    const topFiveCompletionRate = topFiveStatsLocal.totalExpected > 0
      ? (topFiveStatsLocal.completedCount / topFiveStatsLocal.totalExpected) * 100
      : 0;
    const topFiveManagerScoreRate = topFiveStatsLocal.totalExpected > 0
      ? (topFiveStatsLocal.managerScoredCount / topFiveStatsLocal.totalExpected) * 100
      : 0;
    
    // Determine if this is an annual period
    const isAnnual = selectedPeriod.startsWith('Annual');
    
    // For monthly reviews (Life Wheel, SPEAR):
    // - Quarterly: employees × 3 months
    // - Annual: employees × 12 months
    const monthsInPeriod = isAnnual ? 12 : 3;
    const expectedMonthlySubmissions = totalEmployees * monthsInPeriod;
    
    // For quarterly reviews (BSC, PEAR):
    // - Quarterly: 1 per employee
    // - Annual: 4 per employee (one per quarter)
    const quartersInPeriod = isAnnual ? 4 : 1;
    const expectedQuarterlySubmissions = totalEmployees * quartersInPeriod;
    
    const completionRates = {
      // BSC and PEAR are quarterly
      bsc: expectedQuarterlySubmissions > 0 ? (bscScores.length / expectedQuarterlySubmissions) * 100 : 0,
      pear: expectedQuarterlySubmissions > 0 ? (periodPEARs.length / expectedQuarterlySubmissions) * 100 : 0,
      // Life Wheel and SPEAR are monthly
      lifeWheel: expectedMonthlySubmissions > 0 ? (periodLifeWheels.length / expectedMonthlySubmissions) * 100 : 0,
      spear: expectedMonthlySubmissions > 0 ? (periodSpearReviews.length / expectedMonthlySubmissions) * 100 : 0,
    };

    // BSC Perspectives (simplified)
    const perspectives = [
      { name: 'Financial', score: avgBSCScore * 1.05 },
      { name: 'Customer', score: avgBSCScore * 0.98 },
      { name: 'Internal Processes', score: avgBSCScore * 1.02 },
      { name: 'Learning & Growth', score: avgBSCScore * 0.95 },
    ];

    // Calculate active months - for current quarter only show months up to current, past quarters show all
    const now = new Date();
    const currentMonthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const currentQuarter = `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`;
    const isCurrentQuarter = selectedPeriod === currentQuarter;
    
    const activeMonths = isCurrentQuarter
      ? monthlyPeriods.filter((month, idx) => {
          // Parse month to get index
          const [monthName, year] = month.split(' ');
          const monthDate = new Date(`${monthName} 1, ${year}`);
          return monthDate <= now;
        })
      : monthlyPeriods;

    // Goal statistics by month - computed locally from SPEAR data
    // Filter to only include months from March 2026 onwards (feature start date)
    const validGoalMonths = monthlyPeriods.filter(isMonthMarch2026OrLater);
    
    const goalsByMonth: GoalMonthData[] = validGoalMonths.map(month => {
      const shortMonth = month.substring(0, 3);
      const monthSpears = periodSpearReviews.filter(sr => sr.reviewPeriod === month);
      
      // Get all employees who should have goals for this month
      const totalForMonth = totalEmployees;
      
      // Process each SPEAR's monthly incentive goal
      let achieved = 0, progress = 0, failed = 0, pending = 0, withGoalsSet = 0;
      
      monthSpears.forEach(spear => {
        const goal = spear.monthlyIncentiveGoal as MonthlyIncentiveGoal | undefined;
        if (goal?.description) {
          withGoalsSet++;
          const status = getGoalAchievementStatus(goal);
          if (status === 'achieved') achieved++;
          else if (status === 'progress') progress++;
          else if (status === 'failed') failed++;
          else pending++;
        }
      });
      
      return {
        month,
        shortMonth,
        total: totalForMonth,
        achieved,
        progress,
        failed,
        pending,
        withGoalsSet,
      };
    });

    // Aggregate goal statistics (only for valid goal months - March 2026+)
    const goalStatsLocal = {
      totalExpected: validGoalMonths.length > 0 ? totalEmployees * validGoalMonths.length : 0, // Total goals expected (employees × valid months)
      withGoalsSet: goalsByMonth.reduce((sum, m) => sum + m.withGoalsSet, 0),
      achieved: goalsByMonth.reduce((sum, m) => sum + m.achieved, 0),
      progress: goalsByMonth.reduce((sum, m) => sum + m.progress, 0),
      failed: goalsByMonth.reduce((sum, m) => sum + m.failed, 0),
      pending: goalsByMonth.reduce((sum, m) => sum + m.pending, 0),
    };
    const goalAchievementRate = goalStatsLocal.withGoalsSet > 0 
      ? (goalStatsLocal.achieved / goalStatsLocal.withGoalsSet) * 100 
      : 0;
    const goalSetRate = goalStatsLocal.totalExpected > 0
      ? (goalStatsLocal.withGoalsSet / goalStatsLocal.totalExpected) * 100
      : 0;

    return {
      totalEmployees,
      avgBSCScore,
      avgPEARScore,
      gradeDistribution,
      departmentSummaries,
      employeePerformance,
      completionRates,
      perspectives,
      bscSubmissions: bscScores.length,
      bscApproved,
      pearSubmissions: periodPEARs.length,
      pearApproved,
      lifeWheelSubmissions: periodLifeWheels.length,
      lifeWheelSubmitters,
      spearSubmissions: periodSpearReviews.length,
      spearSubmitters,
      monthlyPeriods,
      activeMonths,
      expectedMonthlySubmissions,
      lifeWheelByMonth,
      spearByMonth,
      topFiveByMonth,
      topFiveDrilldownByMonth,
      topFiveStatsLocal,
      topFiveSetRate,
      topFiveCompletionRate,
      topFiveManagerScoreRate,
      goalsByMonth,
      goalStatsLocal,
      goalAchievementRate,
      goalSetRate,
    };
  }, [users, filteredOrgChartData, pearReviews, allLifeWheels, allSpearReviews, filteredDepartments, selectedPeriod, monthlyPeriods, quarterlyPeriods, hasCompanyWideView]);

  // Handlers
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  const handleRefresh = () => {
    refetchPEAR();
    refetchOrgChart();
    refetchLifeWheel();
    refetchSpear();
    refetchCastingTool();
  };

  const handleDepartmentClick = (department: string) => {
    router.push(`/reviews/ceo-dashboard/department/${encodeURIComponent(department)}?period=${selectedPeriod}`);
  };

  const handleEmployeeClick = (employeeId: string) => {
    router.push(`/reviews/ceo-dashboard/employee/${employeeId}?period=${selectedPeriod}`);
  };

  // Determine dashboard title and subtitle based on access level
  const isCSuite = currentUser?.roles?.includes('c-suite');
  const dashboardTitle = hasCompanyWideView ? 'CEO Performance Dashboard' : isCSuite ? 'Team Performance Dashboard' : 'Performance Dashboard';
  const dashboardSubtitle = hasCompanyWideView 
    ? 'Organization-wide performance metrics and insights'
    : `Performance metrics for ${myDepartment || 'your department'} and direct reports`;

  const topFiveDialogRows = useMemo(() => {
    if (!metrics?.topFiveDrilldownByMonth) return [] as TopFiveDrilldownRow[];

    if (selectedTopFiveMonth) {
      return metrics.topFiveDrilldownByMonth[selectedTopFiveMonth] || [];
    }

    const merged = Object.values(metrics.topFiveDrilldownByMonth).flat();
    const uniqueByKey = new Map<string, TopFiveDrilldownRow>();
    merged.forEach((row: TopFiveDrilldownRow) => {
      const key = `${row.userId}-${row.month}`;
      uniqueByKey.set(key, row);
    });

    return Array.from(uniqueByKey.values()).sort((a, b) => {
      if (a.month === b.month) return a.employeeName.localeCompare(b.employeeName);
      return a.month.localeCompare(b.month);
    });
  }, [metrics, selectedTopFiveMonth]);

  const visibleTopFiveRows = useMemo(() => {
    if (topFiveFilter === 'all') return topFiveDialogRows;
    if (topFiveFilter === 'completed') return topFiveDialogRows.filter(r => r.isTopFiveCompleted);
    if (topFiveFilter === 'scored') return topFiveDialogRows.filter(r => r.isManagerScored);
    return topFiveDialogRows.filter(r => !r.hasTopFiveSet || !r.isTopFiveCompleted);
  }, [topFiveDialogRows, topFiveFilter]);

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push('/reviews')}
              sx={{ mb: 1 }}
            >
              Back to Reviews
            </Button>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {dashboardTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dashboardSubtitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={handlePeriodChange}
              >
                {availablePeriods.map((q) => (
                  <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Business Unit</InputLabel>
              <Select
                value={selectedBusinessUnit}
                label="Business Unit"
                onChange={(e) => setSelectedBusinessUnit(e.target.value)}
              >
                {BUSINESS_UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SettingsIcon />}
              onClick={() => setShowGradingConfig(true)}
              sx={{ ml: 1 }}
            >
              Grading Config
            </Button>
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : metrics ? (
          <>
            {/* Key Metrics Row */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MetricCard
                  title="Total Employees"
                  value={metrics.totalEmployees}
                  subtitle={`Across ${(filteredDepartments || []).length} departments`}
                  icon={<PeopleIcon />}
                  color="#1976d2"
                  onClick={() => openEmployeeModal(0, 'All Employees')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MetricCard
                  title="Avg BSC Score"
                  value={metrics.bscSubmissions > 0 ? `${metrics.avgBSCScore.toFixed(1)}%` : 'N/A'}
                  subtitle={`${metrics.bscSubmissions} submitted, ${metrics.bscApproved} approved`}
                  secondarySubtitle={`(${metrics.completionRates.bsc.toFixed(0)}% completion)`}
                  icon={<AssessmentIcon />}
                  color={metrics.bscSubmissions > 0 ? getScoreColor(metrics.avgBSCScore) : '#9e9e9e'}
                  showWarning={metrics.completionRates.bsc < 50}
                  warningText={metrics.completionRates.bsc < 50 ? 'Low participation' : undefined}
                  onClick={() => openEmployeeModal(0, 'BSC Submissions')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MetricCard
                  title="Avg PEAR Score"
                  value={metrics.pearSubmissions > 0 ? `${metrics.avgPEARScore.toFixed(1)}%` : 'N/A'}
                  subtitle={`${metrics.pearSubmissions} submitted, ${metrics.pearApproved} approved`}
                  secondarySubtitle={`(${metrics.completionRates.pear.toFixed(0)}% completion)`}
                  icon={<AssessmentIcon />}
                  color={metrics.pearSubmissions > 0 ? getScoreColor(metrics.avgPEARScore) : '#9e9e9e'}
                  showWarning={metrics.completionRates.pear < 50}
                  warningText={metrics.completionRates.pear < 50 ? 'Low participation' : undefined}
                  onClick={() => openEmployeeModal(1, 'PEAR Submissions')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MonthlyMetricCard
                  title="Life Wheel"
                  monthlyStats={metrics.lifeWheelByMonth}
                  totalSubmitters={metrics.lifeWheelSubmitters}
                  totalEmployees={metrics.totalEmployees}
                  icon={<FavoriteIcon />}
                  color="#e91e63"
                  onClick={() => openEmployeeModal(2, 'Life Wheel Submissions')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MonthlyMetricCard
                  title="SPEAR"
                  monthlyStats={metrics.spearByMonth}
                  totalSubmitters={metrics.spearSubmitters}
                  totalEmployees={metrics.totalEmployees}
                  icon={<DonutLargeIcon />}
                  color="#9c27b0"
                  onClick={() => openEmployeeModal(3, 'SPEAR Submissions')}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                {/* Casting Tool - 4x4 monthly submission card */}
                {(() => {
                  const totalCompleted = castingToolStats?.reduce((sum, dept) => sum + dept.completedThisMonth, 0) || 0;
                  const totalEmployeesForCasting = castingToolStats?.reduce((sum, dept) => sum + dept.totalEmployees, 0) || metrics.totalEmployees;
                  const castingCompletionRate = totalEmployeesForCasting > 0 ? (totalCompleted / totalEmployeesForCasting) * 100 : 0;
                  const currentPeriod = castingToolStats?.[0]?.reviewPeriod || 'Current Month';
                  
                  return (
                    <MetricCard
                      title="Casting Tool (4x4)"
                      value={`${castingCompletionRate.toFixed(0)}%`}
                      subtitle={`${totalCompleted}/${totalEmployeesForCasting} submitted`}
                      secondarySubtitle={`Period: ${currentPeriod}`}
                      icon={<GridViewIcon />}
                      color="#00bcd4"
                      showWarning={castingCompletionRate < 50}
                      warningText={castingCompletionRate < 50 ? 'Low participation' : undefined}
                    />
                  );
                })()}
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <MetricCard
                  title="Overall Completion"
                  value={`${(
                    (metrics.completionRates.bsc * 0.4) + 
                    (metrics.completionRates.pear * 0.4) + 
                    (metrics.completionRates.lifeWheel * 0.1) + 
                    (metrics.completionRates.spear * 0.1)
                  ).toFixed(0)}%`}
                  subtitle="Weighted avg (BSC/PEAR 80%)"
                  icon={<BusinessIcon />}
                  color={metrics.completionRates.bsc >= 80 ? '#4caf50' : '#ff9800'}
                  onClick={() => openEmployeeModal(0, 'Completion Status')}
                />
              </Grid>
            </Grid>

            {/* CEO Top 5 Accomplishments - Monthly Distribution */}
            {metrics.topFiveByMonth.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 3,
                    background: 'linear-gradient(130deg, #f4fbff 0%, #e0f2f1 48%, #fff8e1 100%)',
                    border: '1px solid #d0e7ef',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: '#0d47a1' }}>
                      CEO Top 5 Accomplishments Distribution
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Click a month to drill down into employee completion and scoring details.
                  </Typography>
                  <TopFiveDistributionChart
                    data={metrics.topFiveByMonth}
                    onMonthClick={(month) => openTopFiveDialog(month)}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 3,
                    background: 'linear-gradient(130deg, #ffffff 0%, #eef7ff 52%, #fff3e0 100%)',
                    border: '1px solid #dbe6f3',
                  }}
                >
                  <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: '#1b2a41' }}>
                    CEO Top 5 Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Track how many employees set Top 5 items, completed all five, and received manager scoring.
                  </Typography>

                  <Grid container spacing={1.5} sx={{ mb: 1 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#0288d1' }}>
                          {metrics.topFiveStatsLocal.setCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Top 5 Set</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                          {metrics.topFiveStatsLocal.completedCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: '#6a1b9a' }}>
                          {metrics.topFiveStatsLocal.managerScoredCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Manager Scored</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.25, bgcolor: '#e1f5fe', borderRadius: 2, border: '1px solid #29b6f6' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#0277bd' }}>
                          {metrics.topFiveSetRate.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Set Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.25, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #66bb6a' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                          {metrics.topFiveCompletionRate.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Completion Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.25, bgcolor: '#f3e5f5', borderRadius: 2, border: '1px solid #ab47bc' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#6a1b9a' }}>
                          {metrics.topFiveManagerScoreRate.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Manager Score Rate</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.75} sx={{ mt: 2 }}>
                    <Chip
                      label="Open all months"
                      onClick={() => openTopFiveDialog()}
                      clickable
                      sx={{ fontWeight: 'bold', bgcolor: '#0d47a115', color: '#0d47a1' }}
                    />
                    {metrics.topFiveByMonth.map((monthData) => (
                      <Chip
                        key={monthData.month}
                        label={`${monthData.shortMonth} (${monthData.completedCount}/${monthData.total})`}
                        onClick={() => openTopFiveDialog(monthData.month)}
                        clickable
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
            )}

            {/* Goal Distribution by Month - Only show when there are valid goal months (March 2026+) */}
            {metrics.goalsByMonth.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%)' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      🎯 Monthly Incentive Goal Distribution
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Goal status by month for the selected period (50% salary bonus eligible)
                  </Typography>
                  <GoalDistributionChart data={metrics.goalsByMonth} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #fff3e0 100%)' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Goal Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Overview of monthly incentive goals for the period
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" color="text.primary">
                          {metrics.goalStatsLocal.withGoalsSet}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Goals Set</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" color={metrics.goalSetRate >= 70 ? 'success.main' : 'warning.main'}>
                          {metrics.goalSetRate.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Set Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                        <Typography variant="h4" fontWeight="bold" color={metrics.goalAchievementRate >= 70 ? 'success.main' : metrics.goalAchievementRate >= 40 ? 'warning.main' : 'error.main'}>
                          {metrics.goalAchievementRate.toFixed(0)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Achievement Rate</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#1976d215', borderRadius: 2, border: '1px solid #1976d2' }}>
                        <Typography variant="h5" fontWeight="bold" color="#1976d2">
                          {metrics.goalStatsLocal.achieved}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Achieved</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#ff980015', borderRadius: 2, border: '1px solid #ff9800' }}>
                        <Typography variant="h5" fontWeight="bold" color="#ff9800">
                          {metrics.goalStatsLocal.progress}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Progress</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f4433615', borderRadius: 2, border: '1px solid #f44336' }}>
                        <Typography variant="h5" fontWeight="bold" color="#f44336">
                          {metrics.goalStatsLocal.failed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Failed</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#9e9e9e15', borderRadius: 2, border: '1px solid #9e9e9e' }}>
                        <Typography variant="h5" fontWeight="bold" color="#9e9e9e">
                          {metrics.goalStatsLocal.pending}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            )}

            {/* Grade Distribution & BSC Perspectives */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Grade Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Distribution of BSC grades across the organization
                  </Typography>
                  <GradeDistributionChart data={metrics.gradeDistribution} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    BSC Perspectives
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Average scores by balanced scorecard perspective
                  </Typography>
                  <PerspectivesChart data={metrics.perspectives} />
                </Paper>
              </Grid>
            </Grid>

            {/* Departments Grid */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    Departments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click on a department to view detailed metrics
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={3}>
                {metrics.departmentSummaries.map((dept) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={dept.name}>
                    <DepartmentCard
                      department={dept}
                      onClick={() => handleDepartmentClick(dept.name)}
                      showGoals={isPeriodQ1_2026OrLater(selectedPeriod)}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Top Performers & Need Attention */}
            <Grid container spacing={3}>
              {/* Top Performers */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <TrophyIcon sx={{ color: '#FFD700' }} />
                    <Typography variant="h6" fontWeight="bold">
                      Top Performers
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({metrics.employeePerformance.filter(emp => emp.bscScore !== undefined && emp.bscScore >= 80).length} employees)
                    </Typography>
                  </Stack>
                  <TableContainer sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell width={60} sx={{ bgcolor: 'background.paper' }}>#</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Employee</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Department</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={topSortField === 'bsc'}
                              direction={topSortField === 'bsc' ? topSortOrder : 'desc'}
                              onClick={() => {
                                if (topSortField === 'bsc') {
                                  setTopSortOrder(topSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setTopSortField('bsc');
                                  setTopSortOrder('desc');
                                }
                              }}
                            >
                              BSC
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={topSortField === 'pear'}
                              direction={topSortField === 'pear' ? topSortOrder : 'desc'}
                              onClick={() => {
                                if (topSortField === 'pear') {
                                  setTopSortOrder(topSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setTopSortField('pear');
                                  setTopSortOrder('desc');
                                }
                              }}
                            >
                              PEAR
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={topSortField === 'goals'}
                              direction={topSortField === 'goals' ? topSortOrder : 'desc'}
                              onClick={() => {
                                if (topSortField === 'goals') {
                                  setTopSortOrder(topSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setTopSortField('goals');
                                  setTopSortOrder('desc');
                                }
                              }}
                            >
                              🎯 Goals
                            </TableSortLabel>
                          </TableCell>
                          <TableCell width={50} sx={{ bgcolor: 'background.paper' }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...metrics.employeePerformance]
                          .filter(emp => emp.bscScore !== undefined && emp.bscScore >= 80)
                          .sort((a, b) => {
                            let aVal = 0, bVal = 0;
                            if (topSortField === 'bsc') {
                              aVal = a.bscScore ?? -1;
                              bVal = b.bscScore ?? -1;
                            } else if (topSortField === 'pear') {
                              aVal = a.pearScore ?? -1;
                              bVal = b.pearScore ?? -1;
                            } else if (topSortField === 'goals') {
                              aVal = a.monthlyGoals.filter(g => isMonthMarch2026OrLater(g.month) && g.status === 'achieved').length;
                              bVal = b.monthlyGoals.filter(g => isMonthMarch2026OrLater(g.month) && g.status === 'achieved').length;
                            }
                            return topSortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                          })
                          .map((emp, index) => (
                          <EmployeeRow
                            key={emp.id}
                            employee={emp}
                            rank={index + 1}
                            onClick={() => handleEmployeeClick(emp.id)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Needs Attention */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <WarningIcon sx={{ color: '#ff9800' }} />
                    <Typography variant="h6" fontWeight="bold">
                      Needs Attention
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({metrics.employeePerformance.filter(emp => emp.designation !== 'CEO' && (!emp.hasBSC || (emp.bscScore !== undefined && emp.bscScore < 80))).length} employees)
                    </Typography>
                  </Stack>
                  <TableContainer sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell width={60} sx={{ bgcolor: 'background.paper' }}>#</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Employee</TableCell>
                          <TableCell sx={{ bgcolor: 'background.paper' }}>Department</TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={attentionSortField === 'bsc'}
                              direction={attentionSortField === 'bsc' ? attentionSortOrder : 'asc'}
                              onClick={() => {
                                if (attentionSortField === 'bsc') {
                                  setAttentionSortOrder(attentionSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setAttentionSortField('bsc');
                                  setAttentionSortOrder('asc');
                                }
                              }}
                            >
                              BSC
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={attentionSortField === 'pear'}
                              direction={attentionSortField === 'pear' ? attentionSortOrder : 'asc'}
                              onClick={() => {
                                if (attentionSortField === 'pear') {
                                  setAttentionSortOrder(attentionSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setAttentionSortField('pear');
                                  setAttentionSortOrder('asc');
                                }
                              }}
                            >
                              PEAR
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: 'background.paper' }}>
                            <TableSortLabel
                              active={attentionSortField === 'goals'}
                              direction={attentionSortField === 'goals' ? attentionSortOrder : 'asc'}
                              onClick={() => {
                                if (attentionSortField === 'goals') {
                                  setAttentionSortOrder(attentionSortOrder === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setAttentionSortField('goals');
                                  setAttentionSortOrder('asc');
                                }
                              }}
                            >
                              🎯 Goals
                            </TableSortLabel>
                          </TableCell>
                          <TableCell width={50} sx={{ bgcolor: 'background.paper' }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...metrics.employeePerformance]
                          .filter(emp => emp.designation !== 'CEO' && (!emp.hasBSC || (emp.bscScore !== undefined && emp.bscScore < 80)))
                          .sort((a, b) => {
                            let aVal = 0, bVal = 0;
                            if (attentionSortField === 'bsc') {
                              aVal = a.bscScore ?? -1;
                              bVal = b.bscScore ?? -1;
                            } else if (attentionSortField === 'pear') {
                              aVal = a.pearScore ?? -1;
                              bVal = b.pearScore ?? -1;
                            } else if (attentionSortField === 'goals') {
                              aVal = a.monthlyGoals.filter(g => isMonthMarch2026OrLater(g.month) && g.status === 'achieved').length;
                              bVal = b.monthlyGoals.filter(g => isMonthMarch2026OrLater(g.month) && g.status === 'achieved').length;
                            }
                            return attentionSortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                          })
                          .map((emp, index) => (
                            <EmployeeRow
                              key={emp.id}
                              employee={emp}
                              rank={index + 1}
                              onClick={() => handleEmployeeClick(emp.id)}
                            />
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">
              No data available for the selected period
            </Typography>
          </Box>
        )}

        {/* Top 5 Drill-down Dialog */}
        <Dialog
          open={showTopFiveDialog}
          onClose={() => setShowTopFiveDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#0d47a1' }}>
                  CEO Top 5 Accomplishments Drill-down
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedTopFiveMonth || `${selectedPeriod} (All Months)`} • {visibleTopFiveRows.length} employee records
                </Typography>
              </Box>
              <IconButton onClick={() => setShowTopFiveDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" gap={0.75}>
              <Chip
                label={`All (${topFiveDialogRows.length})`}
                clickable
                color={topFiveFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setTopFiveFilter('all')}
              />
              <Chip
                label={`Completed (${topFiveDialogRows.filter(r => r.isTopFiveCompleted).length})`}
                clickable
                color={topFiveFilter === 'completed' ? 'success' : 'default'}
                onClick={() => setTopFiveFilter('completed')}
              />
              <Chip
                label={`Manager Scored (${topFiveDialogRows.filter(r => r.isManagerScored).length})`}
                clickable
                sx={{
                  bgcolor: topFiveFilter === 'scored' ? '#6a1b9a' : undefined,
                  color: topFiveFilter === 'scored' ? 'white' : undefined,
                }}
                onClick={() => setTopFiveFilter('scored')}
              />
              <Chip
                label={`Pending (${topFiveDialogRows.filter(r => !r.hasTopFiveSet || !r.isTopFiveCompleted).length})`}
                clickable
                color={topFiveFilter === 'pending' ? 'warning' : 'default'}
                onClick={() => setTopFiveFilter('pending')}
              />
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Designation</TableCell>
                    <TableCell align="center">Top 5 Set</TableCell>
                    <TableCell align="center">Completed</TableCell>
                    <TableCell align="center">Manager Scored</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTopFiveRows.map((row) => (
                    <TableRow key={`${row.userId}-${row.month}`} hover>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{row.employeeName}</Typography>
                      </TableCell>
                      <TableCell>{row.department}</TableCell>
                      <TableCell>{row.designation}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.hasTopFiveSet ? 'Yes' : 'No'}
                          color={row.hasTopFiveSet ? 'info' : 'default'}
                          variant={row.hasTopFiveSet ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.isTopFiveCompleted ? 'Complete' : 'Pending'}
                          color={row.isTopFiveCompleted ? 'success' : 'warning'}
                          variant={row.isTopFiveCompleted ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.isManagerScored ? 'Scored' : 'Not Scored'}
                          sx={{
                            bgcolor: row.isManagerScored ? '#6a1b9a' : undefined,
                            color: row.isManagerScored ? 'white' : undefined,
                          }}
                          variant={row.isManagerScored ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.spearId ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setShowTopFiveDialog(false);
                              router.push(`/reviews/spear/review/${row.spearId}`);
                            }}
                          >
                            Review & Score
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setShowTopFiveDialog(false);
                              router.push(`/reviews/ceo-dashboard/employee/${row.userId}?period=${selectedPeriod}`);
                            }}
                          >
                            View Employee
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleTopFiveRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No records found for this filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>

        {/* Employee Completion Modal */}
        <Dialog
          open={showEmployeeModal}
          onClose={() => setShowEmployeeModal(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{modalTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedPeriod} - {metrics?.totalEmployees || 0} total employees
                  {(employeeModalTab === 2 || employeeModalTab === 3) && metrics?.monthlyPeriods && (
                    <> (Monthly: {metrics.monthlyPeriods.join(', ')})</>
                  )}
                </Typography>
              </Box>
              <IconButton onClick={() => setShowEmployeeModal(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
                  <Typography variant="h5" color="primary">
                    {metrics?.bscSubmissions || 0}/{metrics?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">BSC</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fce4ec' }}>
                  <Typography variant="h5" color="secondary">
                    {metrics?.pearSubmissions || 0}/{metrics?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">PEAR</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fce4ec' }}>
                  <Typography variant="h5" sx={{ color: '#e91e63' }}>
                    {metrics?.lifeWheelSubmitters || 0}/{metrics?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Life Wheel</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3e5f5' }}>
                  <Typography variant="h5" sx={{ color: '#9c27b0' }}>
                    {metrics?.spearSubmitters || 0}/{metrics?.totalEmployees || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">SPEAR</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Tabs for report types */}
            <Tabs
              value={employeeModalTab}
              onChange={(_, newValue) => setEmployeeModalTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label={`BSC (${metrics?.bscSubmissions || 0}/${metrics?.totalEmployees || 0})`} />
              <Tab label={`PEAR (${metrics?.pearSubmissions || 0}/${metrics?.totalEmployees || 0})`} />
              <Tab label={`Life Wheel (${metrics?.lifeWheelSubmitters || 0}/${metrics?.totalEmployees || 0})`} />
              <Tab label={`SPEAR (${metrics?.spearSubmitters || 0}/${metrics?.totalEmployees || 0})`} />
            </Tabs>

            {/* BSC Tab */}
            {employeeModalTab === 0 && (
              <Box>
                {/* Submitted */}
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Submitted ({metrics?.employeePerformance.filter(e => e.hasBSC).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Score</TableCell>
                        <TableCell align="center">Grade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => e.hasBSC).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.designation || '-'}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell align="center">{emp.bscScore !== undefined ? formatScore(emp.bscScore) : '-'}</TableCell>
                          <TableCell align="center">
                            <Chip label={emp.bscGrade} size="small" sx={{ backgroundColor: getGradeColor(emp.bscGrade), color: 'white' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Not Submitted */}
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                  <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Not Submitted ({metrics?.employeePerformance.filter(e => !e.hasBSC).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => !e.hasBSC).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell>{emp.designation || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {metrics?.employeePerformance.filter(e => !e.hasBSC).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="success.main" sx={{ py: 2 }}>
                              All employees have submitted their BSC!
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* PEAR Tab */}
            {employeeModalTab === 1 && (
              <Box>
                {/* Submitted */}
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Submitted ({metrics?.employeePerformance.filter(e => e.hasPEAR).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Score</TableCell>
                        <TableCell align="center">Grade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => e.hasPEAR).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.designation || '-'}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell align="center">{emp.pearScore !== undefined ? formatScore(emp.pearScore) : '-'}</TableCell>
                          <TableCell align="center">
                            <Chip label={emp.pearGrade} size="small" sx={{ backgroundColor: getGradeColor(emp.pearGrade), color: 'white' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Not Submitted */}
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                  <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Not Submitted ({metrics?.employeePerformance.filter(e => !e.hasPEAR).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => !e.hasPEAR).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell>{emp.designation || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {metrics?.employeePerformance.filter(e => !e.hasPEAR).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="success.main" sx={{ py: 2 }}>
                              All employees have submitted their PEAR!
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Life Wheel Tab */}
            {employeeModalTab === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Life Wheel is a monthly review. Showing submissions for: {metrics?.monthlyPeriods?.join(', ')}
                </Typography>
                {/* Submitted */}
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Submitted ({metrics?.employeePerformance.filter(e => e.hasLifeWheel).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Submissions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => e.hasLifeWheel).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.designation || '-'}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell align="center">
                            <Chip label={`${emp.lifeWheelCount || 0} / 3`} size="small" color={(emp.lifeWheelCount || 0) >= 3 ? 'success' : 'warning'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Not Submitted */}
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                  <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Not Submitted ({metrics?.employeePerformance.filter(e => !e.hasLifeWheel).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => !e.hasLifeWheel).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell>{emp.designation || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {metrics?.employeePerformance.filter(e => !e.hasLifeWheel).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="success.main" sx={{ py: 2 }}>
                              All employees have submitted their Life Wheel!
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* SPEAR Tab */}
            {employeeModalTab === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  SPEAR is a monthly review. Showing submissions for: {metrics?.monthlyPeriods?.join(', ')}
                </Typography>
                {/* Submitted */}
                <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Submitted ({metrics?.employeePerformance.filter(e => e.hasSpear).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="center">Submissions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => e.hasSpear).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.designation || '-'}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell align="center">
                            <Chip label={`${emp.spearCount || 0} / 3`} size="small" color={(emp.spearCount || 0) >= 3 ? 'success' : 'warning'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Not Submitted */}
                <Typography variant="subtitle2" color="error.main" sx={{ mb: 1 }}>
                  <CancelIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Not Submitted ({metrics?.employeePerformance.filter(e => !e.hasSpear).length || 0})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Position</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics?.employeePerformance.filter(e => !e.hasSpear).map((emp) => (
                        <TableRow key={emp.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleEmployeeClick(emp.id)}>
                          <TableCell>
                            <Typography variant="body2">{emp.name}</Typography>
                          </TableCell>
                          <TableCell>{emp.department || '-'}</TableCell>
                          <TableCell>{emp.designation || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {metrics?.employeePerformance.filter(e => !e.hasSpear).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            <Typography variant="body2" color="success.main" sx={{ py: 2 }}>
                              All employees have submitted their SPEAR!
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Grading Configuration Dialog */}
        <GradingConfigDialog
          open={showGradingConfig}
          onClose={() => setShowGradingConfig(false)}
        />
      </Box>
    </PageWithHeader>
  );
};

export default CEODashboard;
